import { isValidPhoneNumber } from "libphonenumber-js";
import { jwtDecode } from "jwt-decode";
import { TCredentials, TUser } from "../types/context";
import {
  GenericObjectArrayType,
  LoanGrowthItem,
  TActions,
  TransformOptions,
} from "../types/common";
import {
  ACTIVATE,
  APPROVED,
  CLOSE,
  CONST_CREDENTIALS_KEY,
  DATE_FORMAT,
  DEACTIVATE,
  DEFERRED,
  DELETE,
  EDIT,
  FILE_TYPE_DOC,
  FILE_TYPE_DOCX,
  FILE_TYPE_JPEG,
  FILE_TYPE_JPG,
  FILE_TYPE_PDF,
  FILE_TYPE_PNG,
  PENDING,
  RECOMMENDED_FOR_APPROVAL,
  REJECTED,
  SUBMITTED,
  UNLOCK,
  VIEW,
} from "./constants";
import { BASE_API_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { renderSingleFile } from "../components/common/FileViewer";
import { axiosInstance } from "../hooks/common/useAxios";
import styled from "styled-components";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(isoWeek);
dayjs.extend(relativeTime);

export const setCredentials = (value: TCredentials) => {
  try {
    window.localStorage.setItem(CONST_CREDENTIALS_KEY, JSON.stringify(value));
  } catch (e) {
    // saving error
  }
};

export const removeCredentials = () => {
  try {
    localStorage.clear();
  } catch (e) {
    // saving error
  }
};

export const decodeUser = (token: string) => {
  try {
    const decoded: any = jwtDecode(token);
    const user_id = decoded.user_id;
    return { id: user_id, ...decoded.user };
  } catch (err) {
    // saving error
  }
};

export const getCredentials = () => {
  try {
    const res = window.localStorage.getItem(CONST_CREDENTIALS_KEY);
    return res ? JSON.parse(res) : null;
  } catch (e) {
    // saving error
  }
};

export const generateDocumentAuthor = (user: any) => ({
  author_first_name: user?.first_name,
  author_last_name: user?.last_name,
  author_email: user?.email,
  author_phone_number: user?.phone_number || "0709456322",
  created_by: user?.id,
});

export const getAndPrepareFiles = (
  form: any,
  fileFieldNames: string[],
  field: string = "",
) => {
  const uploadedFiles: any = [];
  fileFieldNames.forEach((item) => {
    if (form[item]) {
      uploadedFiles.push(form[item]);
      delete form[item];
    }
  });
  const prefix = field || "attachments";
  return {
    ...form,
    ...prepareAttachmentsPost(uploadedFiles, prefix),
  };
};

export const prepareAttachmentsPost = (
  attachments: any,
  field?: any,
  object?: any,
  noFile?: any,
) => {
  const prefix = field || "attachments";
  const extras = {};
  const atts = (attachments || []).filter((item: any) => noFile || item?.file);
  const inflated_data: any = [];
  atts.forEach((item: any) => {
    if (item?.file) {
      item?.file.forEach((it: any) => {
        inflated_data.push({ ...item, file: it });
      });
    } else {
      inflated_data.push(item);
    }
  });

  inflated_data
    .map((item: any) => ({ ...item, ...object }))
    .forEach((item: any, idx: any) => {
      Object.keys(item).forEach((key) => {
        extras[`${prefix}[${idx}]${key}`] = item[key];
      });
    });
  return extras;
};

export const prepareObjectFormdataPost = (parentObject, field) => {
  const extras = {};
  const object = parentObject[field];
  if (!object) {
    return parentObject;
  }
  Object.keys(object).forEach((key) => {
    extras[`${field}.${key}`] = object[key];
  });
  parentObject = { ...parentObject, ...extras };
  delete parentObject[field];
  return parentObject;
};
export const prepareObjectArrayFormdataPost = (parentObject, field) => {
  const extras = {};
  const object = parentObject[field];
  if (!object) {
    return parentObject;
  }
  object.forEach((item, idx) => {
    if (item) {
      Object.keys(item).forEach((key) => {
        extras[`${field}[${idx}]${key}`] = item[key];
      });
    }
  });
  parentObject = { ...parentObject, ...extras };
  delete parentObject[field];
  return parentObject;
};

export const validateEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase())
    ? null
    : "Please enter a valid email address";
};

export const validatePhoneNumber = (phoneNumber: string) => {
  return isValidPhoneNumber(`${phoneNumber}`)
    ? null
    : "Please enter a valid phone number";
};

export const validateConfirmPass = (
  password: string,
  confirmPassword: string,
) => {
  return password === confirmPassword ? null : "Both passwords should match";
};

export const generateErrorString = (error: any) => {
  let errorString = "";
  Object.keys(error || {}).forEach((key) => {
    errorString += ` ${error[key]}`;
  });
  return errorString;
};

export const timeToString = (minutes: number) => {
  let str = "";
  if (minutes <= 60) {
    str = `${minutes}mins`;
  } else if (minutes <= 1440) {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    str = `${hrs}hrs ${mins}mins`;
  } else {
    const days = Math.floor(minutes / 1440);
    const mins = Math.floor(minutes % 1440);
    const hrs = Math.floor(mins / 60);
    str = `${days}days ${hrs}hrs`;
  }

  return str;
};

export const breakColArray = (array: any[]) => {
  const col1Fields: any[] = [];
  const col2Fields: any[] = [];
  array.forEach((item, idx) => {
    if (idx % 2 === 0) {
      col1Fields.push(item);
    } else {
      col2Fields.push(item);
    }
  });
  return { col1Fields, col2Fields };
};

export const returnObject = (show: boolean, object: any) => {
  return show ? object : [];
};

export const beautify = (message?: string) => {
  return (message || "")
    .replaceAll("_", " ")
    .replaceAll("/", "")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const stringifyError = (message: any) => {
  if (!message) return "An unknown error occured";

  if (typeof message === "string") return message;

  let obj;
  if (Array.isArray(message) && message.length) {
    obj = String(message?.[0]);
  }

  return obj ? String(obj) : "An unknown error occured";
};

export const stringifyName = (user: TUser | null) => {
  return user
    ? `${user?.first_name} ${user?.last_name} ${
        user?.other_name ? user?.other_name : ""
      }`
    : "";
};

export const toPastTense = (verb: string) => {
  // List of irregular verbs (cast to object with string keys)
  const irregularVerbs = {
    go: "went",
    eat: "ate",
    drink: "drank",
    leave: "left",
    cancel: "cancelled",
    delete: "deleted",
    resend_invitation: "resent invitation for",
  } as const;

  // Check if the verb is in the list of irregular verbs
  if (verb in irregularVerbs) {
    return irregularVerbs[verb as keyof typeof irregularVerbs];
  }

  return verb + "ed";
};

export const returnArray = (data: any) => {
  return Array.isArray(data) ? data : [];
};

export const stringifyDate = (date: Date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if the date stored in the variable 'date' is today's date
  if (date.getDate() === today.getDate()) {
    return "Today";
  } else if (date.getDate() === yesterday.getDate()) {
    return "Yesterday";
  } else {
    return dayjs(date).format("ddd MMM DD, YYYY");
  }
};

export const detailsCardView = (item: any) => {
  let new_item: any = {};
  Object.keys(item).map((key) => {
    if (!key.includes("-name"))
      new_item[key] =
        item[`${key}-name`] != null ? item[`${key}-name`] : item[key];
  });
  return new_item;
};

export const stringifyWeeklyDate = (date: Date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if the date stored in the variable 'date' is today's date
  if (date.getDate() === today.getDate()) {
    return "Today";
  } else if (date.getDate() === yesterday.getDate()) {
    return "Yesterday";
  } else {
    return dayjs(date).format("ddd MMM DD, YYYY");
  }
};

export const computerLastWorkingDate = (date: string) => {
  // Get yesterday's date
  const lastDay = dayjs(date).subtract(1, "day");

  // Check if yesterday was a Sunday
  if (lastDay.day() === 0) {
    // Sunday is represented by 0. dayjs is immutable, so return the new value.
    return lastDay.day(-2);
  }

  return lastDay;
};

export const computerNextWorkingDate = (date: string) => {
  const today = dayjs(date);

  // Check if this date is a Friday
  if (today.day() === 5) {
    return today.add(3, "days");
  }

  return today.add(1, "days");
};

export const getTitle = (action?: TActions, postfix?: string) => {
  switch (action) {
    case "create":
      return `Create ${postfix || ""}`;
    case "edit":
      return `Update ${postfix || ""}`;
    case "delete":
      return `Delete ${postfix || ""}`;
    case "activate":
      return "Activate User";
    case "ban":
      return "Deactivate User";
    case "resend_invitation":
      return "Resend Invitation";
    default:
      return "";
  }
};

export const getToastTitle = (action?: TActions, postfix?: string) => {
  switch (action) {
    case "create":
      return `created ${postfix || ""}`;
    case "edit":
      return `updated ${postfix || ""}`;
    case "delete":
      return `deleted ${postfix || ""}`;
    default:
      return "";
  }
};

export const sanitizeAction = (action?: TActions) => {
  switch (action) {
    case "create":
      return "create";
    case "edit":
      return "update";
    case "delete":
      return "delete";
    case "resend_invitation":
      return "resend invitation";
    default:
      return "";
  }
};

export const scoreStype = (value: number) => {
  if (value >= 70) {
    return "success";
  } else if (value >= 50 && value < 70) {
    return "ready";
  } else {
    return "danger";
  }
};

export const getQueryParams = (search: string) => {
  return Object.fromEntries(new URLSearchParams(search));
};

export const mondayThisWeek = () => {
  // Get current date
  const today = dayjs();

  // Calculate Monday of this week
  const mondayOfThisWeek = today.startOf("isoWeek");

  return mondayOfThisWeek.format("YYYY-MM-DD");
};

export const fridayThisWeek = () => {
  // Get current date
  const today = dayjs();

  // Calculate Friday of this week
  const fridayOfThisWeek = today.endOf("isoWeek").subtract(2, "days");

  return fridayOfThisWeek.format("YYYY-MM-DD");
};

export const goBackOneWeek = (date: string) => {
  const today = dayjs(date);
  // Calculate Monday of last week
  const lastWeek = today.subtract(1, "week");
  return lastWeek.format("YYYY-MM-DD");
};

export const stringifyAction = (action: TActions, identifier: string) => {
  switch (action) {
    case "delete":
      return `delete this ${identifier}`;
    default:
      return "";
  }
};

export const getActionToolTip = (action: TActions) => {
  switch (action) {
    case "delete":
      return "Delete";
    case "ban":
      return "Deactivate";
    default:
      return "";
  }
};

export const getPhotoSource = (photo: string) => {
  return photo ? `${BASE_API_URL}${photo}` : "";
};

export const beautifyStringWithUnderscore = (str: string) => {
  return str.replace(/_/g, " ");
};

export const greetings = () => {
  const hour = dayjs().hour();
  if (hour >= 0 && hour < 12) {
    return "Good morning";
  } else if (hour >= 12 && hour < 17) {
    return "Good afternoon";
  } else {
    return "Good evening";
  }
};

export const createColumn = (id: any, hasFilter: boolean = false) => {
  return {
    id,
    header: beautify(id),
    ...returnObject(hasFilter, { filter: {} }),
  };
};

export const formatDate = (value: any, format?: string) => {
  if (!value) return "";
  return dayjs(value).format(format || DATE_FORMAT);
};
export const getAgo = (value: any) => {
  return dayjs(value).fromNow();
};

export const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);

  // Format as: "17/07/2025, 11:46 PM"
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const numberFormat = (value: number) =>
  Number(value || 0).toLocaleString();

export function getRegister(id: string, status?: string) {
  const suffix = status ? `/${status}` : "";
  return `/register${id}${suffix}`;
}

export const formatCurrency = (amount) => {
  const value = Number(amount);
  return new Intl.NumberFormat("en-UG", {
    style: "currency",
    currency: "UGX",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);
};

export function getForm(source_id: string, id?: string) {
  if (id) return `${source_id}/update/${id}`;
  return `${source_id}/create`;
}

export function getDetailsView(source_id: string, id: string) {
  return `${source_id}/view/${id}`;
}

export const getStatusColor = (status: string) => {
  switch (status) {
    case PENDING:
    case SUBMITTED:
      return "gold-inverse";
    case RECOMMENDED_FOR_APPROVAL:
      return "blue-inverse";
    case REJECTED:
      return "red-inverse";
    case APPROVED:
    case "current":
      return "green-inverse";
    case DEFERRED:
      return "orange-inverse";
    default:
      return "";
  }
};

export const createSelectItem = (id: string, name?: string) => {
  return { id, name: name || capitalize(beautify(id)) };
};

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const createEditAction = (url: string, id: string) => {
  return {
    label: EDIT,
    to: getForm(url, id),
  };
};

export const createViewAction = (url: string, id: string) => {
  return {
    label: VIEW,
    to: getDetailsView(url, id),
  };
};

export const createDeleteAction = (url: string, identifier: string) => {
  return {
    label: DELETE,
    deleteUrl: url,
    deleteMessage: `Are you sure you want to delete ${identifier}?`,
  };
};

export const createDeactivateAction = (url: string, identifier: string) => {
  return {
    label: DEACTIVATE,
    deleteUrl: url,
    deleteMessage: `Are you sure you want to deactivate ${identifier}?`,
  };
};

export const createActivateAction = (url: string, identifier: string) => {
  return {
    label: ACTIVATE,
    deleteUrl: url,
    deleteMessage: `Are you sure you want to activate ${identifier}?`,
  };
};

export const createUnlockAction = (url: string, identifier: string) => {
  return {
    label: UNLOCK,
    deleteUrl: url,
    deleteMessage: `Are you sure you want to unlock ${identifier}? Transactions in this period will become editable again.`,
  };
};

// export const createCloseAction = (url: string, identifier: string) => {
//   return {
//     label: CLOSE,
//     deleteUrl: url,
//     deleteMessage: `Are you sure you want to close ${identifier}?`,
//   };
// };

export const createCloseAction = (
  url: string,
  identifier: string,
  reasonField: boolean = false,
) => {
  return {
    label: CLOSE,
    deleteUrl: url,
    deleteMessage: `Are you sure you want to close ${identifier}?`,
    reasonField,
  };
};

export const createCustomAction = (
  url: string,
  label: string,
  icon: any,
  handleCustom: () => void,
) => {
  return {
    label: label,
    to: url,
    icon,
    custom: true,
    handleCustom: () => handleCustom(),
  };
};

export const createViewEdit = (
  url: string,
  id: string,
  shouldEdit: boolean = true,
) => {
  const views = [createViewAction(url, id)];
  shouldEdit && views.push(createEditAction(url, id));
  return views;
};

export function currentYear() {
  return new Date().getFullYear();
}

export function removeEmpty(array: [], key: string) {
  return array?.filter((val) => {
    return val[key] > 0;
  });
}

export const scaleFormatter = (value: any) => {
  let label = Intl.NumberFormat("en", { notation: "compact" }).format(value);
  return label;
};

export function getValidators(sections: any[]) {
  let validators: any = [];
  sections?.map((item) => {
    if (item.fields) validators = [...validators, ...item.fields];
  });
  return validators;
}

const removeUnusedFields = (form_data: any) => {
  if (form_data == null || typeof form_data !== "object") {
    return form_data;
  }
  const ignoredFields = [
    "id",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
    "deleted",
    "declaration",
    "attachments",
  ];
  Object.keys(form_data).forEach((key) => {
    if (ignoredFields.includes(key)) {
      delete form_data[key];
    }
  });
  return form_data;
};
export const objectWithUnsetKeys = (object, keys) => {
  const temp = { ...object };
  keys.forEach((item) => {
    delete temp[item];
  });
  return temp;
};
export const removeUnnecessaryFields = (data: any, keys: string[] = []) => {
  if (Array.isArray(data)) {
    return data.map((it: any) => removeUnusedFields(it));
  }
  const newData = removeUnusedFields(data);
  if (keys) {
    return objectWithUnsetKeys(newData, keys);
  }
  return newData;
};

export const useGetUserPermissions = () => {
  const { user } = useAuth();
  const permissions = user?.role_details?.[0]?.permissions ?? [];
  return permissions?.map((item: any) => item.codename);
};

export const hasPermissions = (permissions: string[]) => {
  const userPermissions = useGetUserPermissions();
  return permissions.some((permission: string) =>
    userPermissions.includes(permission),
  );
};

// check if any of the items exists in an array
export const itemExists = (itemsArray, itemsList) => {
  let available = false;
  itemsList.map((item) => {
    if (itemsArray.includes(item)) {
      available = true;
      return;
    }
  });
  return available;
};

export const isPdf = (file) => {
  return [FILE_TYPE_PDF].includes(file.split(".").pop());
};

export const isMSWord = (file) => {
  return [FILE_TYPE_DOCX, FILE_TYPE_DOC].includes(file.split(".").pop());
};

export const isImage = (file) => {
  return [FILE_TYPE_PNG, FILE_TYPE_JPEG, FILE_TYPE_JPG].includes(
    file.split(".").pop(),
  );
};

export const NoWrap = styled.div`
  white-space: nowrap !important;
`;
export const downloadThroughAnchorLink = (
  downloadUrl,
  fileName,
  params = {},
) => {
  return axiosInstance
    .get(downloadUrl, {
      responseType: "blob",
      params: {
        ...params,
      },
      headers: {
        Authorization: `Bearer ${getCredentials()?.access}`,
      },
    })
    .then((response) => {
      // create file link in browser's memory
      const href = URL.createObjectURL(response.data);

      // create "a" HTML element with href to file & click
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    });
};

const ASYNC_REPORT_POLL_INTERVAL = 3000;
const ASYNC_REPORT_MAX_POLLS = 1200;

const saveBlobAsFile = (blob, fileName) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

const isJsonResponse = (response) =>
  (response.headers["content-type"] || "").includes("application/json");

/**
 * Downloads a report that the backend generates asynchronously.
 *
 * The endpoint is called first with the report filters, which queues the job
 * and returns a `job_id`, then repeatedly with that `job_id` until it responds
 * with the file instead of a JSON status payload. If the very first response is
 * already the file, the endpoint is still synchronous and we just save it.
 */
export const downloadAsyncReport = async (
  downloadUrl,
  fileName,
  params = {},
) => {
  const headers = {
    Authorization: `Bearer ${getCredentials()?.access}`,
  };
  // Requested as a blob so a file response can be saved as-is; JSON payloads
  // are small enough to read back out of the blob.
  const request = (requestParams) =>
    axiosInstance.get(downloadUrl, {
      params: requestParams,
      responseType: "blob",
      headers,
    });

  const initResponse = await request(params);
  if (!isJsonResponse(initResponse)) {
    saveBlobAsFile(initResponse.data, fileName);
    return;
  }

  const initBody = await (initResponse.data as Blob).text();
  const jobId = JSON.parse(initBody)?.job_id;
  if (!jobId) {
    throw new Error(`No job_id in response: ${initBody.slice(0, 200)}`);
  }

  for (let i = 0; i < ASYNC_REPORT_MAX_POLLS; i++) {
    await new Promise((resolve) =>
      setTimeout(resolve, ASYNC_REPORT_POLL_INTERVAL),
    );

    const pollResponse = await request({ job_id: jobId });

    // A non-JSON body means the job is done and this is the file itself.
    if (!isJsonResponse(pollResponse)) {
      saveBlobAsFile(pollResponse.data, fileName);
      return;
    }

    const json = JSON.parse(await (pollResponse.data as Blob).text());
    if (json.status === "failed" || json.error) {
      throw new Error(json.error || "Report generation failed");
    }
  }

  throw new Error("Report is taking too long to generate. Please try again.");
};

// Uploaded documents are stored keyed by their upload field_name in the doc's
// `caption` (see FileInput). Use this to tell whether a record already carries a
// given document, so required file inputs don't demand a re-upload.
export const hasExistingDocument = (attachments: any, caption?: string) =>
  Object.values(attachments || {})
    .flatMap((docs: any) => (Array.isArray(docs) ? docs : []))
    .some((doc: any) => doc?.file && (!caption || doc?.caption === caption));

export const displayDocuments = (docs) => {
  const st = {};
  Object.keys(docs || {})
    .filter((key) => docs[key])
    .map((key) => docs[key])
    .forEach((item) => {
      item.forEach((doc, idx) => {
        const caption = idx > 0 ? idx + 1 : "";
        st[`${doc.caption}_${caption}`] = renderSingleFile(doc);
      });
    });

  return st;
};

export const getNonNullKeys = (
  obj: Record<string, any>,
  excludeKeys: string[] = [],
) => {
  return Object.keys(obj).filter(
    (key) => obj[key] !== null && !excludeKeys.includes(key),
  );
};

export const showObjectItems = (
  obj: Record<string, any>,
  excludeKeys: string[] = [],
) => {
  let cleanedObj: Record<string, any> = {};

  for (let key in obj) {
    if (
      obj[key] !== null &&
      typeof obj[key] !== "object" &&
      !excludeKeys.includes(key)
    ) {
      cleanedObj[key] = obj[key];
    }
  }

  return cleanedObj;
};

export const getResultOptions = (
  data: any[] | Record<string, any>,
  itemName: string | undefined,
  key?: string | undefined | null,
  otherItemName?: string | undefined | null,
) => {
  const results = Array.isArray(data) ? data : data?.results || [];
  return results.length
    ? results.map((item: GenericObjectArrayType) => ({
        id: key ? item[key] : item.id,
        name: beautify(
          `${(itemName && item[itemName]) || ""} ${
            (otherItemName && item[otherItemName]) || ""
          }`.trim() || item.name,
        ),
        data: item,
      }))
    : [];
};

export const getFinancialYearDates = (yearOffset: number = 0) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-indexed

  // If we're before July (month 6), we're in the FY that started last year
  let fyStartYear = currentMonth < 6 ? currentYear - 1 : currentYear;
  fyStartYear += yearOffset;

  const startDate = `${fyStartYear}-07-01`;
  const endDate = `${fyStartYear + 1}-06-30`;

  return { startDate, endDate };
};

//LINE CHARTS

export const filterFutureDisbursements = (data: LoanGrowthItem[]) => {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month() + 1; // dayjs months are 0-based
  const currentDay = now.date();

  return data.filter((item) => {
    // remove future years
    if (item.disbursement_year > currentYear) return false;

    // yearly data has only year -> already valid
    if (!item.disbursement_month) return true;

    // remove future months in current year
    if (
      item.disbursement_year === currentYear &&
      item.disbursement_month > currentMonth
    )
      return false;

    // remove future days in current month
    if (
      item.disbursement_year === currentYear &&
      item.disbursement_month === currentMonth &&
      item.disbursement_day &&
      item.disbursement_day > currentDay
    )
      return false;

    return true;
  });
};

export function transformYearMonthToLineChart<T>(
  rawData: T[] = [],
  options: TransformOptions<T>,
) {
  const {
    label = "Total",
    dateFormat = "MMM YYYY",
    getYear,
    getMonth,
    getValue,
  } = options;

  const sorted = [...rawData].sort(
    (a, b) => getYear(a) - getYear(b) || getMonth(a) - getMonth(b),
  );

  const xLabels = sorted.map((item) =>
    dayjs(`${getYear(item)}-${getMonth(item)}-01`).format(dateFormat),
  );

  const series = [
    {
      label,
      data: sorted.map((item) => getValue(item)),
    },
  ];

  return { xLabels, series };
}

export const transformLoanGrowthData = (
  data: LoanGrowthItem[],
  period: "daily" | "monthly" | "yearly",
  year: number | "all",
) => {
  let filtered = filterFutureDisbursements(data);

  if (period === "monthly" && year !== "all") {
    filtered = filtered.filter((item) => item.disbursement_year === year);
  }

  if (period === "daily") {
    return {
      xLabels: filtered.map((d) =>
        dayjs(
          `${d.disbursement_year}-${d.disbursement_month}-${d.disbursement_day}`,
        ).format("YYYY-MM-DD"),
      ),
      series: [
        {
          label: "Disbursements",
          data: filtered.map((d) => d.count),
        },
      ],
    };
  }

  if (period === "monthly") {
    const monthlyData = filtered.filter(
      (item): item is LoanGrowthItem & { disbursement_month: number } =>
        typeof item.disbursement_month === "number",
    );

    return transformYearMonthToLineChart(monthlyData, {
      label: "Disbursements",
      getYear: (item) => item.disbursement_year,
      getMonth: (item) => item.disbursement_month,
      getValue: (item) => item.count,
    });
  }

  // yearly
  return {
    xLabels: filtered.map((d) => String(d.disbursement_year)),
    series: [
      {
        label: "Disbursements",
        data: filtered.map((d) => d.count),
      },
    ],
  };
};

export function stringify(text: any) {
  return text?.replaceAll?.("_", " ");
}

export const addSelectItem = ({
  value,
  label,
  id = value,
  description,
}: {
  id?: any;
  label?: any;
  value: any;
  description?: any;
}) => {
  return {
    id,
    name: capitalize(stringify(label || value)),
    value,
    description,
  };
};
